import { useEffect, useState } from "react";
import { Admin } from "@/lib/api";
import { AdminLayout } from "./AdminPages";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, Copy, Power, PowerOff } from "lucide-react";
import { confirmDialog, customDialog, toast } from "@/components/ui/dialog";

interface Code {
  id: number;
  code: string;
  reward_type: "points" | "membership";
  points: number;
  plan_id: number | null;
  membership_days: number;
  batch_note: string | null;
  max_uses: number;
  used_count: number;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
}

export function AdminRedeemCodesPage() {
  const [list, setList] = useState<Code[]>([]);
  const [plans, setPlans] = useState<any[]>([]);
  const [filter, setFilter] = useState<"all" | "active" | "used" | "expired">("all");

  const load = () => Admin.redeemCodes().then(setList).catch(() => setList([]));
  useEffect(() => {
    load();
    Admin.plans().then(setPlans).catch(() => {});
  }, []);

  const openCreate = async () => {
    const result = await customDialog<any>((close) => (
      <RedeemCreateForm plans={plans} onConfirm={close} onCancel={() => close(null)} />
    ));
    if (!result) return;
    try {
      const r = await Admin.createRedeemCodes(result);
      toast.success(`已生成 ${r.count} 个兑换码`);
      load();
      // 弹出码列表，便于复制
      await customDialog<null>((close) => (
        <CodeListDialog codes={r.items.map((x) => x.code)} onClose={() => close(null)} />
      ));
    } catch (e: any) {
      toast.error(e?.body?.message || e?.message || "生成失败");
    }
  };

  const toggle = async (c: Code) => {
    try {
      await Admin.updateRedeemCode(c.id, { is_active: !c.is_active });
      toast.success(c.is_active ? "已禁用" : "已启用");
      load();
    } catch (e: any) { toast.error(e?.message || "操作失败"); }
  };

  const remove = async (c: Code) => {
    if (!(await confirmDialog({ title: "删除该兑换码？", description: c.code, destructive: true, confirmText: "删除" }))) return;
    await Admin.delRedeemCode(c.id);
    toast.success("已删除");
    load();
  };

  const filtered = list.filter((c) => {
    if (filter === "active") return c.is_active && c.used_count < c.max_uses && (!c.expires_at || c.expires_at > new Date().toISOString());
    if (filter === "used") return c.used_count >= c.max_uses;
    if (filter === "expired") return !!c.expires_at && c.expires_at <= new Date().toISOString();
    return true;
  });

  return (
    <AdminLayout>
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold">兑换码管理</h1>
              <p className="text-xs text-muted-foreground mt-1">用户在「个人中心」输入兑换码即可领取积分或会员天数。</p>
            </div>
            <Button onClick={openCreate}><Plus className="h-4 w-4 mr-1" /> 生成兑换码</Button>
          </div>

          <div className="flex gap-2 text-sm">
            {[
              ["all", "全部"],
              ["active", "可用"],
              ["used", "已用完"],
              ["expired", "已过期"],
            ].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k as any)}
                className={`px-3 py-1 rounded-full border text-xs transition ${filter === k ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`}>
                {label}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">暂无兑换码</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-xs text-muted-foreground text-left">
                    <th className="py-2 px-2">兑换码</th>
                    <th className="py-2 px-2">奖励</th>
                    <th className="py-2 px-2">使用</th>
                    <th className="py-2 px-2">过期</th>
                    <th className="py-2 px-2">备注</th>
                    <th className="py-2 px-2">状态</th>
                    <th className="py-2 px-2">操作</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((c) => {
                    const expired = !!c.expires_at && c.expires_at <= new Date().toISOString();
                    const usedUp = c.used_count >= c.max_uses;
                    const planName = plans.find((p) => p.id === c.plan_id)?.name || `Plan#${c.plan_id}`;
                    return (
                      <tr key={c.id} className="border-b hover:bg-muted/30">
                        <td className="py-2 px-2 font-mono text-xs">
                          <button onClick={() => { navigator.clipboard.writeText(c.code); toast.info("已复制"); }}
                            className="inline-flex items-center gap-1 hover:text-primary">
                            {c.code} <Copy className="h-3 w-3" />
                          </button>
                        </td>
                        <td className="py-2 px-2">
                          {c.reward_type === "points"
                            ? <span className="text-amber-600">+{c.points} 积分</span>
                            : <span className="text-emerald-600">{planName} +{c.membership_days} 天</span>}
                        </td>
                        <td className="py-2 px-2 text-xs">{c.used_count}/{c.max_uses}</td>
                        <td className="py-2 px-2 text-xs">{c.expires_at?.slice(0, 16).replace("T", " ") || "永久"}</td>
                        <td className="py-2 px-2 text-xs max-w-[12em] truncate">{c.batch_note || "—"}</td>
                        <td className="py-2 px-2 text-xs">
                          {!c.is_active ? <span className="text-muted-foreground">已禁用</span>
                            : expired ? <span className="text-rose-600">已过期</span>
                            : usedUp ? <span className="text-amber-600">已用完</span>
                            : <span className="text-emerald-600">● 可用</span>}
                        </td>
                        <td className="py-2 px-2 space-x-1 whitespace-nowrap">
                          <Button size="sm" variant="ghost" onClick={() => toggle(c)} title={c.is_active ? "禁用" : "启用"}>
                            {c.is_active ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => remove(c)}>
                            <Trash2 className="h-3.5 w-3.5 text-destructive" />
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
}

// ----------- 创建表单 ----------
function RedeemCreateForm({ plans, onConfirm, onCancel }: { plans: any[]; onConfirm: (v: any) => void; onCancel: () => void }) {
  const [form, setForm] = useState({
    count: 1,
    code: "",
    prefix: "",
    reward_type: "points" as "points" | "membership",
    points: 100,
    plan_id: plans[0]?.id || null as number | null,
    membership_days: 30,
    max_uses: 1,
    expires_at: "",
    batch_note: "",
  });

  const submit = () => {
    if (form.reward_type === "points" && form.points <= 0) { toast.warning("积分必须大于 0"); return; }
    if (form.reward_type === "membership") {
      if (!form.plan_id) { toast.warning("请选择会员套餐"); return; }
      if (form.membership_days <= 0) { toast.warning("会员天数必须大于 0"); return; }
    }
    onConfirm({
      count: form.count,
      code: form.count === 1 && form.code.trim() ? form.code.trim() : undefined,
      prefix: form.count > 1 ? form.prefix : undefined,
      reward_type: form.reward_type,
      points: form.reward_type === "points" ? form.points : undefined,
      plan_id: form.reward_type === "membership" ? form.plan_id : undefined,
      membership_days: form.reward_type === "membership" ? form.membership_days : undefined,
      max_uses: form.max_uses,
      expires_at: form.expires_at ? new Date(form.expires_at).toISOString() : undefined,
      batch_note: form.batch_note || undefined,
    });
  };

  return (
    <div className="rounded-xl bg-card shadow-2xl border w-full max-w-lg overflow-hidden max-h-[85vh] flex flex-col">
      <div className="px-5 pt-5 pb-3 border-b">
        <div className="font-semibold">生成兑换码</div>
      </div>
      <div className="p-5 space-y-4 overflow-y-auto">
        <div className="space-y-1.5">
          <Label>奖励类型</Label>
          <div className="grid grid-cols-2 gap-2">
            {[
              { k: "points", label: "积分", desc: "兑换后直接到账" },
              { k: "membership", label: "会员天数", desc: "叠加在已有会员之上" },
            ].map((t) => (
              <button key={t.k} type="button"
                onClick={() => setForm({ ...form, reward_type: t.k as any })}
                className={`text-left rounded-lg border p-3 transition ${form.reward_type === t.k ? "border-primary ring-1 ring-primary bg-primary/5" : "hover:bg-muted/50"}`}>
                <div className="text-sm font-medium">{t.label}</div>
                <div className="text-xs text-muted-foreground">{t.desc}</div>
              </button>
            ))}
          </div>
        </div>

        {form.reward_type === "points" ? (
          <div className="space-y-1.5">
            <Label>积分数量</Label>
            <Input type="number" value={form.points}
              onChange={(e) => setForm({ ...form, points: +e.target.value })} />
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>会员套餐</Label>
              <select className="w-full h-9 rounded-md border bg-background px-3 text-sm"
                value={form.plan_id || ""}
                onChange={(e) => setForm({ ...form, plan_id: e.target.value ? +e.target.value : null })}>
                <option value="">— 请选择 —</option>
                {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label>赠送天数</Label>
              <Input type="number" value={form.membership_days}
                onChange={(e) => setForm({ ...form, membership_days: +e.target.value })} />
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>生成数量</Label>
            <Input type="number" min={1} max={1000} value={form.count}
              onChange={(e) => setForm({ ...form, count: Math.max(1, +e.target.value) })} />
          </div>
          <div className="space-y-1.5">
            <Label>每个码可用次数</Label>
            <Input type="number" min={1} value={form.max_uses}
              onChange={(e) => setForm({ ...form, max_uses: Math.max(1, +e.target.value) })} />
          </div>
        </div>

        {form.count === 1 ? (
          <div className="space-y-1.5">
            <Label>自定义码（可留空随机生成）</Label>
            <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })}
              placeholder="例如 NEWYEAR2026" className="font-mono" />
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label>码前缀（可选）</Label>
            <Input value={form.prefix} onChange={(e) => setForm({ ...form, prefix: e.target.value })}
              placeholder="例如 GIFT，生成 GIFT-XXXXXXXXXX" className="font-mono" />
          </div>
        )}

        <div className="space-y-1.5">
          <Label>过期时间（可选）</Label>
          <Input type="datetime-local" value={form.expires_at}
            onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
        </div>

        <div className="space-y-1.5">
          <Label>批次备注（可选）</Label>
          <Textarea rows={2} value={form.batch_note}
            onChange={(e) => setForm({ ...form, batch_note: e.target.value })}
            placeholder="如：2026 春节运营活动" />
        </div>
      </div>
      <div className="px-5 py-3 bg-muted/30 border-t flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel}>取消</Button>
        <Button size="sm" onClick={submit}>生成</Button>
      </div>
    </div>
  );
}

function CodeListDialog({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const text = codes.join("\n");
  return (
    <div className="rounded-xl bg-card shadow-2xl border w-full max-w-md overflow-hidden">
      <div className="px-5 pt-5 pb-3 border-b">
        <div className="font-semibold">已生成 {codes.length} 个兑换码</div>
        <div className="text-xs text-muted-foreground mt-0.5">建议立即复制保存。</div>
      </div>
      <div className="p-5 space-y-3">
        <Textarea readOnly rows={Math.min(12, codes.length + 1)} value={text} className="font-mono text-xs" />
        <div className="flex gap-2">
          <Button size="sm" variant="outline" className="flex-1"
            onClick={() => { navigator.clipboard.writeText(text); toast.success("已复制全部"); }}>
            <Copy className="h-3.5 w-3.5 mr-1" /> 复制全部
          </Button>
        </div>
      </div>
      <div className="px-5 py-3 bg-muted/30 border-t flex justify-end">
        <Button size="sm" onClick={onClose}>知道了</Button>
      </div>
    </div>
  );
}
